import { useState } from 'react';
import {
  MARGIN_MESSAGE_KEY,
  MAX_MARGIN_PERCENT,
  MIN_MARGIN_PERCENT,
  isMarginPercentOutOfBounds,
  marginMessageParams,
} from '~/utils/moneyBounds';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays';
import { Button, Input, Select } from '~/components/ui';
import {
  formatCurrency,
  extractGraphQLErrorMessages,
  type ClientError,
} from '~/utils';
import {
  useGetBuyerMargins,
  useGetBuyerNegotiatedPrices,
  useSetBuyerMargin,
  useRemoveBuyerMargin,
  useGetMyBuyers,
} from '~/api/organizations';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  platformProductLengthId?: string;
  defaultPriceLabel: string;
};

export function BuyerMarginsModal({
  isOpen,
  onClose,
  platformProductLengthId,
  defaultPriceLabel,
}: Props) {
  const { t } = useTranslation('my-product');
  const { data: marginsData } = useGetBuyerMargins(
    isOpen ? platformProductLengthId : undefined
  );
  const { data: negotiatedPricesData } = useGetBuyerNegotiatedPrices(
    platformProductLengthId,
    isOpen
  );
  const { data: buyersData } = useGetMyBuyers(isOpen);
  const { mutate: setBuyerMargin } = useSetBuyerMargin();
  const { mutate: removeBuyerMargin } = useRemoveBuyerMargin();

  const [newBuyerId, setNewBuyerId] = useState('');
  const [newMargin, setNewMargin] = useState('');
  const [editedMargins, setEditedMargins] = useState<Record<string, string>>(
    {}
  );

  const overrides = marginsData?.buyerMargins ?? [];
  const overriddenIds = new Set(overrides.map((o) => o.buyingOrganization.id));
  const availableBuyers = (buyersData?.myBuyers ?? []).filter(
    (b) => !overriddenIds.has(b.organizationId)
  );
  const negotiatedPriceByBuyerId = new Map(
    (negotiatedPricesData?.buyerNegotiatedPrices ?? []).map((entry) => [
      entry.buyingOrganization.id,
      formatCurrency(entry.price.amount, entry.price.currency),
    ])
  );

  if (!platformProductLengthId) return null;

  const submit = (buyingOrganizationId: string, marginPercent: string) => {
    const percent = Number(marginPercent.trim());
    if (!buyingOrganizationId || !Number.isFinite(percent) || percent < 0) {
      toast.error(t('edit_product.buyer_margins.errors.invalid'));
      return;
    }
    if (isMarginPercentOutOfBounds(percent)) {
      toast.error(t(MARGIN_MESSAGE_KEY, marginMessageParams));
      return;
    }
    setBuyerMargin(
      {
        platformProductLengthId,
        buyingOrganizationId,
        marginRate: percent / 100,
      },
      {
        onSuccess: () => {
          setNewBuyerId('');
          setNewMargin('');
          setEditedMargins((previous) => {
            const next = { ...previous };
            delete next[buyingOrganizationId];
            return next;
          });
          toast.success(t('edit_product.buyer_margins.saved'));
        },
        onError: (error) =>
          toast.error(
            extractGraphQLErrorMessages(error as ClientError).join(', ')
          ),
      }
    );
  };

  const remove = (buyingOrganizationId: string) =>
    removeBuyerMargin(
      { platformProductLengthId, buyingOrganizationId },
      {
        onSuccess: () => toast.success(t('edit_product.buyer_margins.removed')),
        onError: (error) =>
          toast.error(
            extractGraphQLErrorMessages(error as ClientError).join(', ')
          ),
      }
    );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-lg">
        <h2 className="text-base font-semibold text-neut-prim mb-1">
          {t('edit_product.buyer_margins.title')}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('edit_product.buyer_margins.default_note', { defaultPriceLabel })}
        </p>

        <div className="space-y-2">
          {overrides.map((o) => {
            const marginValue =
              editedMargins[o.buyingOrganization.id] ??
              String(Math.round(o.marginRate * 100 * 1_000_000) / 1_000_000);
            return (
              <div
                key={o.id}
                className="grid grid-cols-[1fr_110px_130px_32px] gap-2 items-center"
              >
                <span className="text-sm text-neut-prim">
                  {o.buyingOrganization.name}
                </span>

                <Input
                  leftIcon="%"
                  type="number"
                  min={MIN_MARGIN_PERCENT}
                  max={MAX_MARGIN_PERCENT}
                  name={`margin-${o.buyingOrganization.id}`}
                  value={marginValue}
                  onChange={(_name, value) =>
                    setEditedMargins((previous) => ({
                      ...previous,
                      [o.buyingOrganization.id]: value,
                    }))
                  }
                  onBlur={() => {
                    if (editedMargins[o.buyingOrganization.id] !== undefined) {
                      submit(o.buyingOrganization.id, marginValue);
                    }
                  }}
                />

                <span className="text-sm text-neut-sec">
                  {o.pricePerUnit
                    ? formatCurrency(
                        o.pricePerUnit.amount,
                        o.pricePerUnit.currency
                      )
                    : '—'}
                </span>
                <button
                  type="button"
                  className="text-neut-sec"
                  aria-label={t('edit_product.buyer_margins.remove')}
                  onClick={() => remove(o.buyingOrganization.id)}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-[1fr_90px_auto] gap-2 items-center border-t border-neut-border pt-3 mt-3">
          <Select
            name="newBuyer"
            value={newBuyerId}
            onChange={(_name, value) => setNewBuyerId(value)}
            options={[
              {
                label: t('edit_product.buyer_margins.select_buyer'),
                value: '',
              },
              ...availableBuyers.map((b) => {
                const negotiatedPrice = negotiatedPriceByBuyerId.get(
                  b.organizationId
                );
                return {
                  label: negotiatedPrice
                    ? `${b.organizationName} ${t('edit_product.buyer_margins.negotiated_price_suffix', { price: negotiatedPrice })}`
                    : b.organizationName,
                  value: b.organizationId,
                };
              }),
            ]}
          />
          <Input
            type="number"
            min={MIN_MARGIN_PERCENT}
            max={MAX_MARGIN_PERCENT}
            name="newMargin"
            placeholder="%"
            value={newMargin}
            onChange={(_name, value) => setNewMargin(value)}
          />
          <Button
            size="sm"
            width="auto"
            type="button"
            onClick={() => submit(newBuyerId, newMargin)}
          >
            {t('edit_product.buyer_margins.add')}
          </Button>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            size="sm"
            width="auto"
            variant="accent"
            type="button"
            onClick={onClose}
          >
            {t('edit_product.buyer_margins.done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
